import { createReducer, on } from '@ngrx/store';
import { IEMData } from '@dch/billing-business-logic';
import {
  ICdrDetails,
  IOdbUsageLevel4Response,
} from 'src/app/my-bill/models/odb-usage-level-4.model';
import * as OdbUsageLevel4Actions from '../actions/odb-usage-level-4.actions';
import {
  ISortingInfo,
  SortingOrderType,
} from 'src/app/my-bill/models/odb-usage-l4.model';

export interface IOdbBillUsageLevel4State {
  odbUsageLevel4ByDocumentReference: {
    [documentReference: string]: {
      [sectionOrItemIdentifier: string]: IEMData<IOdbUsageLevel4Response> & {
        code?: string;
      };
    };
  };
  sortingInfo: ISortingInfo;
}

export const initialState: IOdbBillUsageLevel4State = {
  odbUsageLevel4ByDocumentReference: {},
  sortingInfo: { sortingColumn: 'timeStamp', sortingOrder: 'ascending' },
};

const compare = (
  a: ICdrDetails,
  b: ICdrDetails,
  columnName: string,
  sortingOrder: SortingOrderType
) => {
  let comaparableA = a[columnName];
  let comaparableB = b[columnName];
  if (columnName === 'duration') {
    comaparableA = a[columnName].value;
    comaparableB = b[columnName].value;
  }
  if (
    (columnName === 'amount' || columnName === 'duration') &&
    sortingOrder === 'descending'
  ) {
    return comaparableB - comaparableA;
  } else if (
    (columnName === 'amount' || columnName === 'duration') &&
    sortingOrder === 'ascending'
  ) {
    return comaparableA - comaparableB;
  } else if (sortingOrder === 'ascending') {
    if (comaparableA < comaparableB) {
      return -1;
    }
    if (comaparableA > comaparableB) {
      return 1;
    }
    return 0;
  } else {
    return comaparableA > comaparableB ? -1 : 0;
  }
};

export const odbUsageLevel4Reducer = createReducer(
  initialState,
  on(
    OdbUsageLevel4Actions.getOdbUsageLevel4Request,
    (state, { documentReference, sectionOrItemIdentifier }) => ({
      ...state,
      odbUsageLevel4ByDocumentReference: {
        ...state.odbUsageLevel4ByDocumentReference,
        [documentReference]: {
          ...state.odbUsageLevel4ByDocumentReference[documentReference],
          [sectionOrItemIdentifier]: {
            result: null,
            isFetching: true,
            hasError: false,
          },
        },
      },
    })
  ),
  on(
    OdbUsageLevel4Actions.getOdbUsageLevel4Success,
    (state, { documentReference, sectionOrItemIdentifier, payload }) => {
      const sortedCdrDetails = [...payload.cdrDetails].sort((a, b) =>
        compare(a, b, 'timeStamp', 'ascending')
      );
      return {
        ...state,
        odbUsageLevel4ByDocumentReference: {
          ...state.odbUsageLevel4ByDocumentReference,
          [documentReference]: {
            ...state.odbUsageLevel4ByDocumentReference[documentReference],
            [sectionOrItemIdentifier]: {
              result: {
                ...payload,
                cdrDetails: sortedCdrDetails,
              },
              isFetching: false,
              hasError: false,
            },
          },
        },
      };
    }
  ),
  on(
    OdbUsageLevel4Actions.getSortedOdbUsageByColumn,
    (state, { documentReference, sectionOrItemIdentifier, sortingColumn }) => {
      let sortingOrder: SortingOrderType = 'ascending';
      if (state.sortingInfo.sortingColumn === sortingColumn) {
        sortingOrder =
          state.sortingInfo.sortingOrder === 'ascending'
            ? 'descending'
            : 'ascending';
      }

      const sortedCdrDetails = [
        ...state.odbUsageLevel4ByDocumentReference[documentReference][
          sectionOrItemIdentifier
        ].result.cdrDetails,
      ].sort((a, b) => compare(a, b, sortingColumn, sortingOrder));

      const odbUsageLevel4ByDocumentReference =
        state.odbUsageLevel4ByDocumentReference[documentReference][
          sectionOrItemIdentifier
        ].result;

      const odbUsageLevel4Response = {
        ...odbUsageLevel4ByDocumentReference,
        cdrDetails: sortedCdrDetails,
      };
      return {
        ...state,
        odbUsageLevel4ByDocumentReference: {
          ...state.odbUsageLevel4ByDocumentReference,
          [documentReference]: {
            ...state.odbUsageLevel4ByDocumentReference[documentReference],
            [sectionOrItemIdentifier]: {
              result: odbUsageLevel4Response,
              isFetching: false,
              hasError: false,
            },
          },
        },
        sortingInfo: {
          sortingColumn: sortingColumn,
          sortingOrder: sortingOrder,
        },
      };
    }
  ),
  on(
    OdbUsageLevel4Actions.getOdbUsageLevel4Failure,
    (state, { documentReference, sectionOrItemIdentifier, code }) => ({
      ...state,
      odbUsageLevel4ByDocumentReference: {
        ...state.odbUsageLevel4ByDocumentReference,
        [documentReference]: {
          ...state.odbUsageLevel4ByDocumentReference[documentReference],
          [sectionOrItemIdentifier]: {
            result: null,
            isFetching: false,
            hasError: true,
            code,
          },
        },
      },
    })
  )
);
